class RebuyClient {
  constructor({ clientToken }) {
    this.maxCacheMinutes = 30;
    this.storageKey = "productJSON";
    this.clientToken = clientToken;
  }

  async query(url, params) {
    return fetch(
      `https://rebuyengine.com/${url}?${new URLSearchParams({
        key: this.clientToken,
        ...params,
      })}`,
      {
        method: "GET",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
      },
    )
      .then((res) => res.json())
      .then((res) => {
        return res;
      });
  }

  async getProductsByIDs(productIDs) {
    const products = [];
    const staticIDs = [];

    productIDs.forEach((productID) => {
      const cachedProduct = this.getProductFromCacheByID(productID);

      if (cachedProduct) {
        products.push(cachedProduct);
      } else {
        staticIDs.push(productID);
      }
    });

    if (staticIDs.length > 0) {
      const response = await this.query("api/v1/products/static", {
        metafields: "yes",
        shopify_product_ids: staticIDs.join(","),
        limit: 100,
      });

      response.data.forEach((product) => {
        this.saveProductToCacheByID(product.id, product);
      });

      products.push(...response.data);
    }

    return products.sort((a, b) => {
      return productIDs.indexOf(a.id) - productIDs.indexOf(b.id);
    });
  }

  async getProductByID(productID) {
    const cachedProduct = this.getProductFromCacheByID(productID);

    if (cachedProduct) {
      return cachedProduct;
    }

    console.log("Calling RebuyEngine API for product ID:", productID);

    const response = await this.query("api/v1/products/static", {
      metafields: "yes",
      shopify_product_ids: productID,
      limit: 1,
      format: "pretty",
    });

    console.log("RebuyEngine API response:", response);

    if (response.data && response.data.length > 0) {
      response.data.forEach((product) => {
        this.saveProductToCacheByID(product.id, product);
      });
      return response.data[0];
    } else {
      console.error("No product data returned from RebuyEngine API");
      return null;
    }
  }

  async getProductByHandle(handle, callback = () => {}) {
    const cachedProduct = this.getProductFromCacheByHandle(handle);

    if (cachedProduct) {
      return callback(cachedProduct);
    }

    const response = await this.query("api/v1/products/static", {
      metafields: "yes",
      handles: handle,
      limit: 1,
    });

    response.data.forEach((product) => {
      this.saveProductToCacheByHandle(product.handle, product);
    });

    return callback(response.data[0]);
  }

  getProductHandle(path = window.location.pathname) {
    const handles = path.match(/\/products\/([^\/]*).*$/);
    return handles ? handles[1] : null;
  }

  getProductFromCacheByID(productID) {
    const cacheMax = Date.now() - this.maxCacheMinutes * 60 * 1000;
    const productJSON = JSON.parse(
      localStorage.getItem(`${this.storageKey}:${productID}`),
    );
    return productJSON && productJSON.cachedAt > cacheMax ? productJSON : null;
  }

  getProductFromCacheByHandle(handle) {
    const cacheMax = Date.now() - this.maxCacheMinutes * 60 * 1000;
    const productJSON = JSON.parse(
      localStorage.getItem(`${this.storageKey}:${handle}`),
    );
    return productJSON && productJSON.cachedAt > cacheMax ? productJSON : null;
  }

  saveProductToCacheByID(productID, productJSON) {
    productJSON.cachedAt = Date.now();
    this._safeCacheWrite(
      `${this.storageKey}:${productID}`,
      JSON.stringify(productJSON),
    );
  }

  saveProductToCacheByHandle(handle, productJSON) {
    productJSON.cachedAt = Date.now();
    this._safeCacheWrite(
      `${this.storageKey}:${handle}`,
      JSON.stringify(productJSON),
    );
  }

  // Writes to localStorage with QuotaExceededError protection
  _safeCacheWrite(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      if (e.name === "QuotaExceededError") {
        this._evictOldestEntries();
        try {
          localStorage.setItem(key, value);
        } catch (_) {}
      }
    }
  }

  // Evicts the oldest half of rebuy-related cache entries from localStorage
  _evictOldestEntries() {
    const rebuyPrefixes = [
      "productJSON:",
      "metaobject_",
      "product_video_media_",
      "product_video_only_",
      "product_video_filtered_",
      "product_grouping_",
      "product_",
    ];
    const entries = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (rebuyPrefixes.some((p) => key.startsWith(p))) {
        try {
          const parsed = JSON.parse(localStorage.getItem(key));
          entries.push({ key, cachedAt: parsed?.cachedAt || 0 });
        } catch (_) {
          entries.push({ key, cachedAt: 0 });
        }
      }
    }
    entries.sort((a, b) => a.cachedAt - b.cachedAt);
    const removeCount = Math.max(1, Math.ceil(entries.length / 2));
    for (let i = 0; i < removeCount; i++) {
      localStorage.removeItem(entries[i].key);
    }
  }

  removeProductFromCacheByID(productID) {
    localStorage.removeItem(`${this.storageKey}:${productID}`);
  }

  removeProductFromCacheByHandle(handle) {
    localStorage.removeItem(`${this.storageKey}:${handle}`);
  }
}
class ShopifyStorefrontClient extends RebuyClient {
  constructor({ store, apiAccessToken, apiVersion = "2026-01", clientToken }) {
    super({ clientToken });
    this.store = store;
    this.apiVersion = apiVersion;
    this.apiAccessToken = apiAccessToken;
  }

  async query(query, variables = {}) {
    try {
      const response = await fetch(
        `https://${this.store}/api/${this.apiVersion}/graphql.json`,
        {
          method: "POST",
          body: JSON.stringify({ query, variables }),
          headers: {
            "Content-type": "application/json; charset=UTF-8",
            "X-Shopify-Storefront-Access-Token": this.apiAccessToken,
          },
        },
      );

      const result = await response.json();

      if (result.errors) {
        console.error("GraphQL Errors:", result.errors);
        return null;
      }

      return result?.data;
    } catch (error) {
      console.error("API Request Error:", error);
      return null;
    }
  }

  // Fetches data with localStorage caching and a TTL matching RebuyClient's maxCacheMinutes
  async fetchWithCache(cacheKey, query, variables = {}) {
    const cacheMax = Date.now() - this.maxCacheMinutes * 60 * 1000;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.cachedAt > cacheMax) {
          return parsed.data;
        }
        localStorage.removeItem(cacheKey);
      } catch (err) {
        localStorage.removeItem(cacheKey);
      }
    }

    const data = await this.query(query, variables);

    try {
      localStorage.setItem(
        cacheKey,
        JSON.stringify({ data, cachedAt: Date.now() }),
      );
    } catch (e) {
      if (e.name === "QuotaExceededError") {
        console.warn(
          "localStorage quota exceeded, evicting oldest cache entries",
        );
        this._evictOldestEntries();
        try {
          localStorage.setItem(
            cacheKey,
            JSON.stringify({ data, cachedAt: Date.now() }),
          );
        } catch (_) {}
      }
    }
    return data;
  }

  async getMetaobjectByIdCached(id) {
    const query = `
      query MetaobjectById($id: ID!) {
        node(id: $id) {
          ... on Metaobject {
            id
            type
            fields {
              key
              value
              type
              reference {
                ... on MediaImage {
                  id
                  image {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
      }
    `;

    return this.fetchWithCache(`metaobject_${id}`, query, { id });
  }

  async getProductWithVideoMedia(id) {
    const query =
      "query ProductWithVideoMedia($id: ID!) { product(id: $id) { id title handle media(first: 50) { edges { node { mediaContentType ... on Video { id sources { url mimeType format height width } } ... on ExternalVideo { id host embeddedUrl } } } } } }";

    return this.fetchWithCache(`product_video_media_${id}`, query, { id });
  }

  async getProductVideoMediaOnly(id) {
    const query =
      "query ProductVideoMediaOnly($id: ID!) { product(id: $id) { id title handle media(first: 50) { edges { node { mediaContentType ... on Video { id sources { url mimeType format height width } } ... on ExternalVideo { id host embeddedUrl } } } } } }";
    return this.fetchWithCache(`product_video_only_${id}`, query, { id });
  }
  async getProductByGID(id) {
    const query = `query ProductById($id: ID!) {
      product(id: $id) {
        id
        title
        handle
        productType
        publishedAt
        createdAt
        updatedAt
        featuredImage {
          url
          id
        }
        variants(first: 250) {
          edges {
            node {
              id
              title
              price {
                amount
                currencyCode
              }
              compareAtPrice {
                amount
                currencyCode
              }
              sku
              taxable
              product {
                id
              }
              image {
                url
                id
              }
              selectedOptions {
                name
                value
              }
            }
          }
        }
      }
    }`;
    return this.fetchWithCache(`product_${id}`, query, { id });
  }
  async getProductVideoMediaFiltered(id) {
    const query =
      "query ProductVideoMediaFiltered($id: ID!) { product(id: $id) { id title handle media(first: 50) { edges { node { mediaContentType ... on Video { id sources { url mimeType format height width } } ... on ExternalVideo { id host embeddedUrl } } } } } }";

    return this.fetchWithCache(`product_video_filtered_${id}`, query, { id });
  }
  async getProductGroupingByTag(product) {
    const productTag = "product-group::";

    let tagsArray;
    if (Array.isArray(product.tags)) {
      tagsArray = product.tags;
    } else {
      tagsArray = product.tags.split(",").map((tag) => tag.trim());
    }

    const groupingTag = tagsArray.find((tag) =>
      tag.toLowerCase().includes(productTag),
    );

    if (!groupingTag) return null;

    const groupingBase = groupingTag.trim();
    const groupingBaseLower = groupingBase.toLowerCase();
    const searchQuery = `tag:"${groupingBaseLower}" OR tag:"${groupingBaseLower} ~"`;

    const query = `
      query ProductGroupingByTag($searchQuery: String!) {
        products(first: 50, query: $searchQuery) {
          nodes {
            id
            title
            featuredImage {
              url
            }
            media(first: 10) {
              nodes {
                id
                mediaContentType
                alt
                ... on MediaImage {
                  image {
                    url
                    altText
                    height
                    width
                  }
                }
              }
            }
            tags
          }
        }
      }
    `;
    const cacheKey = `product_grouping_${groupingBaseLower.replace(
      /[^a-zA-Z0-9]/g,
      "_",
    )}`;

    return this.fetchWithCache(cacheKey, query, { searchQuery });
  }
}
const apiAccessToken =
  window.Rebuy?.shop?.api_key || "144ef41094675f07042f1f234e3e82b7d9b92201";
const shopifyAccessToken =
  window.Rebuy?.shop?.storefront_access_token ||
  "c07b54b8d79fbc731cf6ebfc4ae80769";
const shopifyStore =
  window.Shopify?.shop || "sugarloafsocialclub.myshopify.com";
const rebuyClient = new RebuyClient({
  clientToken: apiAccessToken,
});
const shopifyClient = new ShopifyStorefrontClient({
  store: shopifyStore,
  apiAccessToken: shopifyAccessToken,
  apiVersion: "2026-01",
});

// Resolves color-pattern metaobject refs and stitches swatchColors onto the product
async function stitchSwatchColors(product) {
  const raw = product?.metafields?.shopify?.["color-pattern"];

  if (!raw) return;
  let gids;
  if (Array.isArray(raw)) {
    gids = raw;
  } else if (typeof raw === "string") {
    if (raw.startsWith("gid://")) {
      gids = [raw];
    } else {
      try {
        const parsed = JSON.parse(raw);
        gids = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        gids = [raw];
      }
    }
  } else {
    gids = [raw];
  }

  gids = gids.filter(
    (gid) => typeof gid === "string" && gid.startsWith("gid://"),
  );
  if (gids.length === 0) return;

  const metaobjectPromises = gids.map((gid) =>
    shopifyClient.getMetaobjectByIdCached(gid),
  );

  const results = await Promise.all(metaobjectPromises);

  product.swatchColors = [];

  for (const result of results) {
    const node = result?.node;
    if (!node?.fields) continue;

    const fields = {};
    for (const field of node.fields) {
      fields[field.key] = field;
    }

    const label = fields.label?.value;
    if (!label) continue;

    const swatch = { label };

    if (fields.image?.reference?.image?.url) {
      swatch.background = `url(${fields.image.reference.image.url}&width=72)`;
    } else if (fields.color?.value) {
      swatch.background = fields.color.value;
    }

    product.swatchColors.push(swatch);

    window.theme = window.theme || {};
    window.theme.colors = window.theme.colors || {};
    if (!window.theme.colors[label]) {
      window.theme.colors[label] = swatch.background;
    }
  }
}

document.addEventListener("rebuy.productsChange", async (event) => {
  const widget = event.detail.widget;
  if (!widget.data.products) return;

  const processPromises = widget.data.products.map(async (product) => {
    await stitchSwatchColors(product);

    return product;
  });

  await Promise.all(processPromises);

  const vue = widget?.View?.vue;
  if (vue?.$forceUpdate) {
    vue.$nextTick(() => vue.$forceUpdate());
  }
});
